// 📄 utils/apiCollectionParser.ts
// Parser 100% client-side de colecciones de API (OpenAPI/Swagger JSON y
// Postman Collection v2.x) para "Importar colección" en el Catálogo de
// APIs Estándar (ver components/shared/ImportarColeccionModal.tsx). Mismo
// criterio que curlParser.ts: sin librerías externas (JSON.parse nativo),
// nunca importa credenciales — los headers de autenticación se detectan y
// se omiten, se cuentan aparte para avisar al admin.
import { ApiEndpointConfig, ApiEndpointMetodo, ApiEndpointParam } from "../services/clienteConexionService.ts";

export type FormatoColeccion = "openapi" | "postman";

const METODOS_VALIDOS: ApiEndpointMetodo[] = ["GET", "POST", "PUT", "PATCH", "DELETE"];

// Mismo criterio que HEADERS_CREDENCIALES en curlParser.ts, más
// "authorization" explícito — acá siempre se omite (no hay modo "proyecto"
// que sí las importe, esto solo alimenta el Catálogo de referencia).
const HEADERS_AUTH = ["authorization", "apikey", "api-key", "x-api-key"];
const esHeaderAuth = (nombre: string): boolean => HEADERS_AUTH.includes((nombre || "").trim().toLowerCase());

export interface EndpointColeccion {
  // Id temporal SOLO para la selección en el wizard — el id final del
  // ApiEndpointConfig se genera recién al confirmar (ver
  // endpointColeccionAApiEndpointConfig más abajo).
  id: string;
  metodo: ApiEndpointMetodo;
  path: string;
  descripcion: string;
  params: ApiEndpointParam[];
  headers: ApiEndpointParam[];
  pathVariables: ApiEndpointParam[];
  headersOmitidos: string[];
}

export interface ResultadoParseoColeccion {
  ok: boolean;
  error?: string;
  formato?: FormatoColeccion;
  nombreColeccion?: string;
  versionLabel?: string;
  endpoints: EndpointColeccion[];
  // Endpoints descartados por tener un método HTTP no soportado (ver
  // METODOS_VALIDOS) — se omiten silenciosamente de la lista, solo se
  // cuentan para el resumen post-importación.
  omitidos: number;
}

// ── Detección de formato ────────────────────────────────────────────────

export const detectarFormato = (json: any): FormatoColeccion | null => {
  if (!json || typeof json !== "object") return null;
  if (typeof json.openapi === "string" || typeof json.swagger === "string") return "openapi";

  const info = json.info;
  if (info && typeof info === "object") {
    if (typeof info._postman_id === "string") return "postman";
    if (typeof info.schema === "string" && info.schema.toLowerCase().includes("postman")) return "postman";
  }
  return null;
};

// ── OpenAPI / Swagger ───────────────────────────────────────────────────

// OpenAPI usa "{param}" — OneHub usa ":param" (ver utils/pathVariables.ts,
// que es lo que ya sabe leer el editor de Path Variables existente). Se
// convierte acá para que las variables importadas queden funcionales de
// una, no solo como texto.
const convertirPathOpenApi = (path: string): string => path.replace(/\{([^}]+)\}/g, (_m, nombre) => `:${nombre}`);

const valorEjemploParametro = (param: any): string => {
  if (param?.example !== undefined) return String(param.example);
  if (param?.schema?.example !== undefined) return String(param.schema.example);
  if (param?.schema?.default !== undefined) return String(param.schema.default);
  return "";
};

const versionLabelOpenApi = (json: any): string => {
  if (typeof json.openapi === "string") {
    const [major, minor] = json.openapi.split(".");
    return `OpenAPI ${major}.${minor ?? "0"}`;
  }
  return `Swagger ${json.swagger}`;
};

const METODOS_A_IGNORAR_OPENAPI = ["parameters", "summary", "description", "servers"];

const parsearOpenApi = (json: any): ResultadoParseoColeccion => {
  const nombreColeccion = json.info?.title || "Colección sin nombre";
  const versionLabel = versionLabelOpenApi(json);
  const endpoints: EndpointColeccion[] = [];
  let omitidos = 0;
  let contador = 0;

  const paths = json.paths && typeof json.paths === "object" ? json.paths : {};

  for (const pathKey of Object.keys(paths)) {
    const pathItem = paths[pathKey];
    if (!pathItem || typeof pathItem !== "object") continue;

    // Parámetros a nivel de path (compartidos por todos los métodos de ese
    // path) — se combinan con los del método puntual, que tienen prioridad
    // si repiten el mismo name+in (criterio de la spec OpenAPI 3.x).
    const parametrosPathItem: any[] = Array.isArray(pathItem.parameters) ? pathItem.parameters : [];

    for (const metodoRaw of Object.keys(pathItem)) {
      if (metodoRaw.startsWith("$") || METODOS_A_IGNORAR_OPENAPI.includes(metodoRaw)) continue;
      const metodo = metodoRaw.toUpperCase() as ApiEndpointMetodo;
      if (!METODOS_VALIDOS.includes(metodo)) { omitidos++; continue; }

      const operation = pathItem[metodoRaw];
      if (!operation || typeof operation !== "object") continue;

      const parametrosOperacion: any[] = Array.isArray(operation.parameters) ? operation.parameters : [];
      const mapaParametros = new Map<string, any>();
      [...parametrosPathItem, ...parametrosOperacion].forEach((p) => {
        if (p?.name && p?.in) mapaParametros.set(`${p.in}:${p.name}`, p);
      });
      const parametros = [...mapaParametros.values()];

      const params: ApiEndpointParam[] = parametros
        .filter((p) => p.in === "query")
        .map((p) => ({ clave: p.name, valor: valorEjemploParametro(p) }));

      const headers: ApiEndpointParam[] = [];
      const headersOmitidos: string[] = [];
      parametros.filter((p) => p.in === "header").forEach((p) => {
        if (esHeaderAuth(p.name)) { headersOmitidos.push(p.name); return; }
        headers.push({ clave: p.name, valor: valorEjemploParametro(p) });
      });

      const pathConvertido = convertirPathOpenApi(pathKey);
      const pathVariables: ApiEndpointParam[] = parametros
        .filter((p) => p.in === "path")
        .map((p) => ({ clave: p.name, valor: "" }));
      // Fallback por si el doc no declaró el parámetro "in: path" pero el
      // path sí trae "{nombre}" — no debería pasar en OpenAPI bien
      // formado, pero mejor no perder la variable si ocurre.
      const clavesExistentes = new Set(pathVariables.map((v) => v.clave));
      let match: RegExpExecArray | null;
      const regexPath = /\{([^}]+)\}/g;
      while ((match = regexPath.exec(pathKey)) !== null) {
        if (!clavesExistentes.has(match[1])) { pathVariables.push({ clave: match[1], valor: "" }); clavesExistentes.add(match[1]); }
      }

      endpoints.push({
        id: `ep-${contador++}`,
        metodo,
        path: pathConvertido,
        descripcion: operation.summary || operation.description || "",
        params,
        headers,
        pathVariables,
        headersOmitidos
      });
    }
  }

  return { ok: true, formato: "openapi", nombreColeccion, versionLabel, endpoints, omitidos };
};

// ── Postman Collection v2.x ─────────────────────────────────────────────

interface ItemPostmanPlano {
  nombre: string;
  request: any;
}

// Las carpetas de una colección Postman son items con `item[]` propio y
// sin `request` — se recorren recursivamente para juntar todos los
// requests reales sin importar en qué nivel de anidamiento estén.
const aplanarItemsPostman = (items: any[]): ItemPostmanPlano[] => {
  const resultado: ItemPostmanPlano[] = [];
  for (const item of items || []) {
    if (!item || typeof item !== "object") continue;
    if (Array.isArray(item.item)) {
      resultado.push(...aplanarItemsPostman(item.item));
      continue;
    }
    if (item.request) resultado.push({ nombre: item.name || "", request: item.request });
  }
  return resultado;
};

const versionLabelPostman = (json: any): string => {
  const schema: string = json.info?.schema || "";
  const match = schema.match(/v(\d+(?:\.\d+)?)/i);
  return match ? `Postman v${match[1]}` : "Postman";
};

// Prioriza url.path[] (estructurado) sobre url.raw: Postman separa el host
// (incluyendo variables tipo "{{baseUrl}}") de los segmentos del path, así
// que reconstruir desde path[] evita mezclar el placeholder del host en el
// endpoint — coherente con que urlBase es un campo aparte en OneHub (ver
// ImportarColeccionModal.tsx, no se toca en esta importación). Solo cae a
// `raw` si el JSON no trae la forma estructurada; ahí sí puede quedar
// basura del host que se intenta recortar de forma best-effort.
const construirPathPostman = (url: any): string => {
  if (url && Array.isArray(url.path) && url.path.length > 0) {
    return "/" + url.path.join("/");
  }

  const raw: string = typeof url === "string" ? url : url?.raw || "";
  let p = raw.split("?")[0];
  p = p.replace(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\/[^/]+/, "");
  p = p.replace(/^\{\{[^}]+\}\}/, "");
  if (!p) return "/";
  return p.startsWith("/") ? p : `/${p}`;
};

const parsearPostman = (json: any): ResultadoParseoColeccion => {
  const nombreColeccion = json.info?.name || "Colección sin nombre";
  const versionLabel = versionLabelPostman(json);
  const itemsPlanos = aplanarItemsPostman(json.item);
  const endpoints: EndpointColeccion[] = [];
  let omitidos = 0;
  let contador = 0;

  for (const item of itemsPlanos) {
    const request = item.request;
    const metodo = String(request?.method || "").toUpperCase() as ApiEndpointMetodo;
    if (!METODOS_VALIDOS.includes(metodo)) { omitidos++; continue; }

    const url = request.url;
    const path = construirPathPostman(url);

    const headersRaw: any[] = Array.isArray(request.header) ? request.header : [];
    const headers: ApiEndpointParam[] = [];
    const headersOmitidos: string[] = [];
    headersRaw.filter((h) => !h?.disabled).forEach((h) => {
      if (esHeaderAuth(h.key)) { headersOmitidos.push(h.key); return; }
      headers.push({ clave: h.key || "", valor: h.value || "" });
    });

    const queryRaw: any[] = Array.isArray(url?.query) ? url.query : [];
    const params: ApiEndpointParam[] = queryRaw
      .filter((q) => !q?.disabled)
      .map((q) => ({ clave: q.key || "", valor: q.value || "" }));

    // Path Variables declaradas explícitamente (url.variable[], Postman
    // v2.1) + fallback por regex sobre el path ya reconstruido, mismo
    // criterio que el fallback de OpenAPI arriba (no perder variables que
    // el JSON no haya declarado prolijamente).
    const variablesDeclaradas: any[] = Array.isArray(url?.variable) ? url.variable : [];
    const pathVariables: ApiEndpointParam[] = variablesDeclaradas.map((v) => ({ clave: v.key || "", valor: "" }));
    const clavesExistentes = new Set(pathVariables.map((v) => v.clave));
    let match: RegExpExecArray | null;
    const regexPath = /:([A-Za-z_][A-Za-z0-9_]*)/g;
    while ((match = regexPath.exec(path)) !== null) {
      if (!clavesExistentes.has(match[1])) { pathVariables.push({ clave: match[1], valor: "" }); clavesExistentes.add(match[1]); }
    }

    endpoints.push({
      id: `ep-${contador++}`,
      metodo,
      path,
      descripcion: item.nombre,
      params,
      headers,
      pathVariables,
      headersOmitidos
    });
  }

  return { ok: true, formato: "postman", nombreColeccion, versionLabel, endpoints, omitidos };
};

// ── Punto de entrada ─────────────────────────────────────────────────────

export const parsearColeccion = (jsonTexto: string): ResultadoParseoColeccion => {
  let json: any;
  try {
    json = JSON.parse(jsonTexto);
  } catch {
    return { ok: false, error: "El archivo no es un JSON válido.", endpoints: [], omitidos: 0 };
  }

  const formato = detectarFormato(json);
  if (formato === "openapi") return parsearOpenApi(json);
  if (formato === "postman") return parsearPostman(json);

  return {
    ok: false,
    error: "Formato no reconocido. Se aceptan OpenAPI JSON y colecciones Postman v2.0/v2.1.",
    endpoints: [],
    omitidos: 0
  };
};

const generarId = (): string => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

// Mapea un endpoint ya detectado (y seleccionado por el admin) al modelo
// real de OneHub — recién acá se genera el id definitivo. `descripcion` no
// tiene campo propio en ApiEndpointConfig (ver clienteConexionService.ts),
// se guarda como `nombre` (el alias legible que ya existe en el modelo).
export const endpointColeccionAApiEndpointConfig = (e: EndpointColeccion): ApiEndpointConfig => ({
  id: generarId(),
  nombre: e.descripcion,
  endpoint: e.path,
  metodo: e.metodo,
  bodyEjemplo: "",
  params: e.params,
  headers: e.headers,
  pathVariables: e.pathVariables
});
